import type { FC, PropsWithChildren } from 'react'
import styled from '@emotion/styled'
import { useTheme } from '@emotion/react'
import type { Theme } from '@emotion/react'

import { InfoIcon } from '@segment/matcha/components/Icons/Common'

import Badge from '@segment/matcha/components/Badge'
import Button from '@segment/matcha/components/Button'
import ColumnTrack from '@segment/matcha/components/ColumnTrack'
import Typography from '@segment/matcha/components/Typography'
import Divider from '@segment/matcha/components/Divider'
import Paper from '@segment/matcha/components/Paper'
import Tooltip from '@segment/matcha/components/Tooltip'

import SectionBase, * as Section from '@segment/matcha/recipes/Section'

import { trackButtonClick } from '@segment/tracking/scripts'

import getAbsoluteUrl from '@segment/utils/getAbsoluteUrl'

import { usePageConfig } from '@segment/contexts/page'
import type { PageConfig } from '@segment/contexts/page'

type PricingCards = {
  badge: string
  title: string
  excerpt: string
  button: JSX.Element
  price: string
  benefitsHeading: string
  benefitsSubheading: string
  benefits: (string | (string | JSX.Element)[])[]
  optionalBenefits?: (string | (string | JSX.Element)[])[]
}

const getCards = (config: PageConfig, theme: Theme): PricingCards[] => [
  {
    badge: `Profiles`,
    title: `Unify `,
    excerpt: `Unify enables teams to combine real-time events with warehouse data to build unified profiles and empower any team within the business to use trusted data to build 1:1 personalized experiences at scale.
`,
    button: (
      <StyledButton
        size="medium"
        color="primary"
        variant="outlined"
        href={getAbsoluteUrl(`/demo/`, config)}
        onClick={() =>
          trackButtonClick({
            buttonText: `Get a demo`,
            targetUrl: `/demo/`,
            buttonId: `pricing-cdp-page-unify-plan-demo`,
          })
        }
      >
        Get a demo
      </StyledButton>
    ),
    price: `Custom Pricing`,
    benefitsHeading: `What you can do with Unify:`,
    benefitsSubheading: `Enable engineering teams to:`,
    benefits: [
      `Collect and unify real-time events with customer data in the warehouse`,
      `Automatically generate identity-resolved profiles`,
      `Enrich and activate unified profile data to 450+ destinations`,
    ],
  },
  {
    badge: `Profiles + AI`,
    title: `Unify Plus`,
    excerpt: `Unify Plus provides your teams with all the capabilities in Unify and equips your business teams to create more targeted profiles using calculated and AI-powered predictive traits.
`,
    button: (
      <StyledButton
        size="medium"
        color="primary"
        variant="outlined"
        href={getAbsoluteUrl(`/demo/`, config)}
        onClick={() =>
          trackButtonClick({
            buttonText: `Get a demo`,
            targetUrl: `/demo/`,
            buttonId: `pricing-cdp-page-unify-plus-plan-demo`,
          })
        }
      >
        Get a demo
      </StyledButton>
    ),
    price: `Custom Pricing`,
    benefitsHeading: `Everything in Unify AND:`,
    benefitsSubheading: `Enable marketing teams to:`,
    benefits: [`Create computed traits`, `Leverage AI to create predictive traits`],
  },
  {
    badge: `Adaptive Customer Engagement`,
    title: `Engage`,
    excerpt: `Engage is the most complete real-time CDP that enables your technical and business users to build and activate customer profiles and audiences anywhere.
`,
    button: (
      <StyledButton
        size="medium"
        color="primary"
        variant="filled"
        href={getAbsoluteUrl(`/demo/`, config)}
        onClick={() =>
          trackButtonClick({
            buttonText: `Get a demo`,
            targetUrl: `/demo/`,
            buttonId: `pricing-cdp-page-engage-plan-demo`,
          })
        }
      >
        Get a demo
      </StyledButton>
    ),
    price: `Custom Pricing`,
    benefitsHeading: `Everything in Unify Plus AND:`,
    benefitsSubheading: `Enable marketing teams to:`,
    benefits: [
      `Create and manage audiences`,
      `Build AI-powered predictive and generative audiences`,
      `Orchestrate customer journeys and campaigns`,
    ],
    optionalBenefits: [
      [
        `Audience & profile enrichment`,
        <Tooltip label="Expand identity-resolved profiles to include all warehouse data" key="info-icon">
          <InfoIcon color={theme.palette.twilio.gray[40]} />
        </Tooltip>,
      ],
      [
        `AI-recommended audiences`,
        <Tooltip
          label="Build more targeted audiences based on AI-powered product recommendations designed to drive conversions"
          key="info-icon"
        >
          <InfoIcon color={theme.palette.twilio.gray[40]} />
        </Tooltip>,
      ],
    ],
  },
]

const PlansCards: FC<PropsWithChildren<unknown>> = () => {
  const pageConfig = usePageConfig()
  const theme = useTheme()

  return (
    <Wrapper>
      <StyledSectionBase variant="centered">
        <Section.Contents>
          <ColumnTrack columns={1} overrides={{ lg: 3 }}>
            {getCards(pageConfig, theme).map((card, index) => {
              return (
                <StyledPaper key={index} elevation={3}>
                  <div>
                    <Badge variant="filledBlue">{card.badge}</Badge>
                    <Typography tag="h2" variant="h2">
                      {card.title}
                    </Typography>
                  </div>
                  <Typography variant="pMedium">{card.excerpt}</Typography>
                  <Price variant="pLarge">{card.price}</Price>
                  {card.button}
                  <StyledDivider />
                  <div>
                    <ListHeading variant="pMedium">{card.benefitsHeading}</ListHeading>
                    <ListSubheading variant="pMedium">{card.benefitsSubheading}</ListSubheading>
                    <List>
                      {card.benefits.map((benefit, index) => (
                        <StyledListItem key={`benefit-${index}`}>
                          <Benefit variant="pMedium">
                            {Array.isArray(benefit) ? benefit.map((item) => item) : benefit}
                          </Benefit>
                        </StyledListItem>
                      ))}
                    </List>
                    {card.optionalBenefits && (
                      <>
                        <ListSubheading variant="pMedium" key="Optional add-on" tag="p">
                          Optional add-ons
                        </ListSubheading>
                        <List>
                          {card.optionalBenefits.map((benefit, index) => (
                            <StyledListItem key={`benefit-${index}`}>
                              <Benefit variant="pMedium">
                                {Array.isArray(benefit) ? benefit.map((item) => item) : benefit}
                              </Benefit>
                            </StyledListItem>
                          ))}
                        </List>
                      </>
                    )}
                  </div>
                </StyledPaper>
              )
            })}
          </ColumnTrack>
        </Section.Contents>
      </StyledSectionBase>
    </Wrapper>
  )
}

const Wrapper = styled.div`
  background-color: ${({ theme }) => theme.palette.twilio.green[10]};
`

const StyledSectionBase = styled(SectionBase)`
  max-width: 1320px;

  ${Section.Wrapper} {
    padding-top: 0;

    ${({ theme }) => theme.breakpoints.down(`md`)} {
      row-gap: 40px;
    }
  }
`

const StyledDivider = styled(Divider)`
  margin-top: 24px;
`

const StyledPaper = styled(Paper)`
  display: grid;
  background-color: ${({ theme }) => theme.palette.neutrals.white[100]};
  padding: 24px;
  grid-template-rows: 10% 25% repeat(4, min-content) 1fr;
  max-width: 327px;
  justify-self: center;
  ${({ theme }) => theme.breakpoints.up(`lg`)} {
    padding: 32px;
    grid-template-rows: 10% 22.5% repeat(4, min-content) 1fr;
    max-width: 352px;
  }
`

const StyledButton = styled(Button)`
  width: 100%;
  height: 56px;
`

const Price = styled(Typography)`
  overflow: hidden;
  white-space: nowrap;
  font-weight: 600;
`

const ListHeading = styled(Typography)`
  font-weight: 600;
`
const ListSubheading = styled(Typography)`
  margin-bottom: 8px;
`

const List = styled.ul`
  margin-bottom: 24px;
  padding-left: 24px;

  ${({ theme }) => theme.breakpoints.up(`lg`)} {
    margin-bottom: 0;
  }
`

const StyledListItem = styled.li`
  list-style-image: url("data:image/svg+xml;charset=UTF-8,%3csvg width='12' height='9' fill='none' xmlns='http://www.w3.org/2000/svg'%3e%3cpath fill-rule='evenodd' clip-rule='evenodd' d='M11.045.667a.91.91 0 0 1 .025 1.305L5.031 8.126a.95.95 0 0 1-1.356 0L.93 5.33a.91.91 0 0 1 .025-1.305.954.954 0 0 1 1.331.025l2.067 2.106L9.714.692a.954.954 0 0 1 1.33-.025Z' fill='%23121C2D'/%3e%3c/svg%3e ");

  font-weight: 400;
  padding: 0 0 8px 8px;
`

const Benefit = styled(Typography)`
  margin: 0;
  display: flex;
  gap: 8px;
`
export default PlansCards
